import { useContext, useEffect, useState } from "react";
import IProcessed_Report from "@models/IProcessed_Report";
import IOnGetSalesByStateDTO, { ISalesByStateGroup } from "@models/DTO/IOnGetSalesByStateDTO";
import { Link } from "react-router-dom";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IDownloadReportExcelDTO from "../../../../models/DTO/IDownloadReportExcelDTO";
import IOnDownloadReportExcelDTO from "../../../../models/DTO/IOnDownloadReportExcelDTO";
import UserService from "../../../../services/UserService";
import UIService from "../../../../services/UIService";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import ReportPeriod from "../ReportPeriod/ReportPeriod";
import "./SalesByStateReport.scss";

type Props = {
    processedReport: IProcessed_Report;
};

export const SalesByStateReport = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const reportData = props.processedReport.report_data as IOnGetSalesByStateDTO;

    const [collapsedStates, setCollapsedStates] = useState<Set<string>>(new Set());
    const [downloadingExcel, setDownloadingExcel] = useState(false);

    // Sort state groups: alphabetical, "Unknown" last
    const sortedStateGroups: ISalesByStateGroup[] = reportData?.stateGroups
        ? [...reportData.stateGroups].sort((a, b) => {
              if (a.state === "Unknown") return 1;
              if (b.state === "Unknown") return -1;
              return a.state.localeCompare(b.state);
          })
        : [];

    // Socket listeners for Excel download
    useEffect(() => {
        socket.on(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);

        return () => {
            socket.removeListener(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownloadExcel = () => {
        setDownloadingExcel(true);

        const payload: IDownloadReportExcelDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            reportGuid: props.processedReport.guid,
        };

        socket.emit(EndpointsEnum.ReportDownloadExcel, payload);
    };

    const onDownloadExcel = (data: IOnDownloadReportExcelDTO) => {
        setDownloadingExcel(false);

        if (data.downloadDirect && data.buffer) {
            downloadFromBase64(data.buffer, data.filename);
        } else if (data.s3Url) {
            window.open(data.s3Url, "_blank");
        }
    };

    const downloadFromBase64 = (base64: string, filename: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const toggleStateGroup = (state: string) => {
        setCollapsedStates((prev) => {
            const next = new Set(prev);
            if (next.has(state)) {
                next.delete(state);
            } else {
                next.add(state);
            }
            return next;
        });
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
    };

    const isLight = UIService.isMainViewLight();

    return (
        <div className="SalesByStateReport">
            <ReportPeriod className={"mb-3"} startDate={props.processedReport.period_start} endDate={props.processedReport.period_end} />
            {/* Header with Excel download */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <OwLabel
                        className="me-3"
                        title={downloadingExcel ? "generating_excel" : "excel"}
                        leftIconClass={downloadingExcel ? "fas fa-spinner fa-spin" : "fas fa-download"}
                        fn={handleDownloadExcel}
                    />
                </div>
                <div className="d-flex gap-4">
                    <span>
                        <strong>Total Orders:</strong> {reportData?.totalOrders || 0}
                    </span>
                    <span>
                        <strong>Grand Total:</strong> {formatCurrency(reportData?.grandTotal || 0)}
                    </span>
                </div>
            </div>

            {/* State Groups */}
            {sortedStateGroups.length === 0 ? (
                <div className="text-muted">No data available.</div>
            ) : (
                <div>
                    {sortedStateGroups.map((group) => {
                        const isCollapsed = collapsedStates.has(group.state);

                        return (
                            <div key={group.state} className={"state-group mb-3"}>
                                {/* State Header */}
                                <div
                                    className={
                                        "state-group-header d-flex justify-content-between align-items-center p-2 " +
                                        (isLight ? "bg-light" : "secondary-bg-color")
                                    }
                                    onClick={() => toggleStateGroup(group.state)}
                                    style={{ cursor: "pointer" }}>
                                    <div className="d-flex align-items-center">
                                        <i className={"fas fa-chevron-" + (isCollapsed ? "right" : "down") + " me-2"} />
                                        <strong>{group.state}</strong>
                                        <span className="badge bg-secondary ms-2">{group.orderCount} orders</span>
                                    </div>
                                    <strong>{formatCurrency(group.subtotal)}</strong>
                                </div>

                                {/* Orders Table */}
                                {!isCollapsed && (
                                    <table className={"table table-sm table-striped mb-0 " + (isLight ? "" : "table-dark")}>
                                        <thead>
                                            <tr>
                                                <th>Order #</th>
                                                <th>Customer</th>
                                                <th>City</th>
                                                <th>State</th>
                                                <th className="text-end">Total</th>
                                                <th>Description / Tags</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.orders.map((order) => (
                                                <tr key={order.orderGuid}>
                                                    <td>
                                                        <Link to={`/orders/${order.orderGuid}/entry`}>{order.orderNo}</Link>
                                                    </td>
                                                    <td>{order.customerName}</td>
                                                    <td>{order.city}</td>
                                                    <td>{order.state}</td>
                                                    <td className="text-end">{formatCurrency(order.grandTotal)}</td>
                                                    <td>
                                                        {order.description && <span className="me-2">{order.description}</span>}
                                                        {order.tags?.map((tag) => (
                                                            <span key={tag} className="badge bg-info me-1">
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </td>
                                                    <td>{UserService.parseDate(order.statusDate)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        );
                    })}

                    {/* Grand Total Footer */}
                    <div
                        className={
                            "d-flex justify-content-between align-items-center p-2 mt-2 fw-bold " +
                            (isLight ? "bg-light" : "secondary-bg-color")
                        }>
                        <span>Grand Total ({reportData?.totalOrders || 0} orders)</span>
                        <span>{formatCurrency(reportData?.grandTotal || 0)}</span>
                    </div>
                </div>
            )}
        </div>
    );
};
